import {useConfig} from '@aboutyou/config';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {CampaignTopBanner} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/campaign_top_banner.types';
import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useTrackPromotion} from '@aboutyou/tracking';
import {
  DeprecatedDiscountBanner as DeprecatedDiscountBannerBase,
  DiscountBanner as DiscountBannerBase,
  ModalDialog,
} from '@aboutyou/ui-components';
import {UserGenders} from '@aboutyou/user';
import React, {useEffect} from 'react';
import {useIsNewBannerFrameworkActive} from '../../../features/PromotionBanner/utils/useIsNewBannerFrameworkActive';
import {useSessionGender} from '../../../shared-deprecated/sessionGender';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {isAppInstallIncentiveCampaign} from '../discountsHelper';
import {useGetGenderBanner} from '../hooks/useGetGenderBanner';

export interface DiscountBannerViewProps {
  className?: string;
  reducedSize?: boolean;
  onClick?: () => void;
  hideSimpleCampaign?: boolean;
  showOnlySimpleCampaign?: boolean;
}

const SetCssVariableForReducedHeight = () => {
  // need to wrap it into useEffect as its failing for desktop SSR
  useEffect(() => {
    document.body.style.setProperty(
      '--quick-view-discount-banner-height',
      '36px',
    );

    return () => {
      document.body.style.removeProperty('--quick-view-discount-banner-height');
    };
  }, []);

  return null;
};

const DiscountBanner = ({
  className,
  activeDiscount,
  onClick,
  reducedSize,
  asSimpleBanner,
  hideCountdown,
}: {
  className?: string;
  activeDiscount?: GetDiscountsResponse_Discount;
  onClick?: VoidFunction;
  reducedSize?: boolean;
  asSimpleBanner?: boolean;
  hideCountdown: boolean;
}) => {
  const getGenderBanner = useGetGenderBanner();
  const activateNewBannerFramework = useIsNewBannerFrameworkActive();
  const {t} = useTranslation();

  if (!activeDiscount) {
    return null;
  }

  const discount = getGenderBanner(activeDiscount);
  const campaign = activeDiscount;
  if (!discount || !campaign) {
    return null;
  }

  const isAppOnlyDiscount = isAppInstallIncentiveCampaign(campaign.campaign);

  if (activateNewBannerFramework) {
    return (
      <>
        <TrackBannerPromotion
          genderDiscount={discount}
          activeDiscount={campaign}
        />
        <DiscountBannerBase
          className={className}
          headline={discount.headlineText}
          endsAt={
            !hideCountdown && discount.showCountdown
              ? campaign.campaign.period.endDate
              : undefined
          }
          conditionText={discount.conditionText}
          link={isAppOnlyDiscount ? undefined : activeDiscount.linkTarget?.url}
          onClick={onClick}
          foregroundColor={activeDiscount.colors.foregroundColor.hex}
          backgroundColor={activeDiscount.colors.backgroundColor.hex}
          countdownForegroundColor={
            activeDiscount.countdownColors.foregroundColor.hex
          }
          countdownBackgroundColor={
            activeDiscount.countdownColors.backgroundColor.hex
          }
          reducedSize={reducedSize}
          infoButtonLabel={t('Open info window')}
          closeButtonLabel={t('Close')}
          ContentPopperLayoutComponent={ModalDialog}
        />
      </>
    );
  }

  return (
    <>
      <TrackBannerPromotion
        genderDiscount={discount}
        activeDiscount={campaign}
      />
      <DeprecatedDiscountBannerBase
        className={className}
        reducedSize={reducedSize}
        asSimpleBanner={asSimpleBanner}
        title={discount.headlineText}
        endDate={
          discount.showCountdown ? campaign.campaign.period.endDate : undefined
        }
        headlineText={discount.headlineText}
        conditionText={discount.conditionText}
        // this should be a BE behaviour, but doing it in FE as the bug  time-sensitive
        link={isAppOnlyDiscount ? undefined : activeDiscount.linkTarget?.url}
        backgroundColor={discount.backgroundColor}
        onClick={onClick}
        hideCountdown={hideCountdown}
      />
    </>
  );
};

export const DiscountBannerView = ({
  className,
  discount,
  reducedSize,
  onClick,
}: GetDiscountsResponse & DiscountBannerViewProps) => {
  const {isActive: isBasHugoTakeoverActive} = useIsFeatureActive(
    'bas_homepage_campaign_hugo',
  );
  const {device} = useConfig();
  const {gender} = useSessionGender();
  const {t} = useTranslation();
  const hideCountdown = useIsUrgencyTriggersDisabled();

  if (!discount && !isBasHugoTakeoverActive) {
    return null;
  }

  if (isBasHugoTakeoverActive && device === 'desktop') {
    return (
      <DeprecatedDiscountBannerBase
        className={className}
        reducedSize={reducedSize}
        asSimpleBanner
        title={t(
          'Ready to explore HUGO Blue? More fashion, more attitude & more denim.',
        )}
        link={gender === UserGenders.Male ? '/s/hugo-106028' : '/s/hugo-106027'}
        backgroundColor={'#003594'}
        onClick={onClick}
        hideCountdown={hideCountdown}
      />
    );
  }

  const isAppOnlyDiscount = discount
    ? isAppInstallIncentiveCampaign(discount.campaign)
    : false;

  return (
    <>
      {reducedSize && <SetCssVariableForReducedHeight />}
      <DiscountBanner
        className={className}
        reducedSize={reducedSize}
        activeDiscount={discount}
        asSimpleBanner={isAppOnlyDiscount}
        onClick={onClick}
        hideCountdown={hideCountdown}
      />
    </>
  );
};

const TrackBannerPromotion = ({
  genderDiscount,
  activeDiscount,
}: {
  genderDiscount: CampaignTopBanner;
  activeDiscount: GetDiscountsResponse_Discount;
}) => {
  const trackPromotion = useTrackPromotion();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    trackPromotion({
      promotion: {
        campaign: {
          name: activeDiscount.campaign.campaignName,
          id: activeDiscount.campaign.campaignId,
          destinationId: genderDiscount.path.split('?')[0]?.split('-').pop(),
          path: genderDiscount.path,
          linkedContent: 'top_banner',
        },
        source: TrackingPromotionCreative.Banner,
      },
      streamPosition: 0,
      actionType: 'impression',
    });
  }, []);

  return null;
};
